import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useLayoutEffect, useRef } from 'react';

import { useRealtimeEvent } from './useRealtimeEvent';

/**
 * Refetch a query whenever the server signals that its data moved.
 *
 * The signals carry no payload by design: the server only says "this module changed" to
 * the people already allowed to see it, and the refetch goes through the ordinary REST
 * endpoint, which authorises the request as usual. Nothing readable travels over the
 * socket, so a widget can subscribe without widening what its viewer can see.
 */
export function useRefetchOnRealtime(event: string, queryKey: readonly unknown[]): void {
  const queryClient = useQueryClient();
  // Call sites pass an inline array, so its identity changes every render. Holding the
  // latest one in a ref (updated in a layout effect, as useRealtimeEvent does for handlers)
  // keeps the callback — and therefore the subscription — stable.
  const keyRef = useRef(queryKey);
  useLayoutEffect(() => {
    keyRef.current = queryKey;
  });

  const onChanged = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: keyRef.current });
  }, [queryClient]);

  useRealtimeEvent(event, onChanged);
}
